import { APP_SETTINGS } from '../config/appSettings'

export const NATIVE_INTENT_KINDS = [
  'capture',
  'search',
  'open-note',
  'share-text',
  'share-url',
  'share-image',
  'share-images',
  'paste-image',
] as const

export const NATIVE_INTENT_STATES = [
  'received',
  'active',
  'web-ready',
  'offered',
  'acknowledged',
  'completed',
  'failed',
] as const

export type NativeIntentKind = typeof NATIVE_INTENT_KINDS[number]
export type NativeIntentState = typeof NATIVE_INTENT_STATES[number]
export type NativeIntentPayload = Record<string, unknown>

export interface NativeIntentEnvelope {
  id: string
  kind: NativeIntentKind
  source: string
  schemaVersion: number
  createdAt: number
  updatedAt: number
  payload: NativeIntentPayload
  revision: number
  state: NativeIntentState
  attemptCount: number
  expiresAt: number
  acknowledgedRevision: number | null
  failureCode: string | null
}

export interface NativeShareFileRecord {
  token: string
  name: string
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp' | 'image/heic' | 'image/heif'
  size: number
  lastModified: number
}

export interface NativeSharePayload {
  /** Optional title from Android EXTRA_SUBJECT/EXTRA_TITLE; absent on older envelopes. */
  sourceTitle?: string
  body: string
  files: NativeShareFileRecord[]
}

export interface NativeEditorImagePayload {
  noteId: string
  files: [NativeShareFileRecord]
}

const KIND_SET = new Set<string>(NATIVE_INTENT_KINDS)
const STATE_SET = new Set<string>(NATIVE_INTENT_STATES)
// Match Android's canonical UUID contract without constraining the version nibble.
// Note routes already support UUIDv7 IDs, while native envelopes currently use v4.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isFiniteInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number.isFinite(value)
}

export function parseNativeIntentEnvelope(value: unknown): NativeIntentEnvelope {
  if (!isRecord(value)) throw new Error('Native intent envelope must be an object.')
  if (typeof value.id !== 'string' || !UUID_PATTERN.test(value.id)) {
    throw new Error('Native intent ID is invalid.')
  }
  if (typeof value.kind !== 'string' || !KIND_SET.has(value.kind)) {
    throw new Error('Native intent kind is unsupported.')
  }
  if (typeof value.source !== 'string' || !/^[a-z0-9-]{1,64}$/.test(value.source)) {
    throw new Error('Native intent source is invalid.')
  }
  if (value.schemaVersion !== APP_SETTINGS.nativeIntents.schemaVersion) {
    throw new Error('Native intent schema version is unsupported.')
  }
  if (typeof value.state !== 'string' || !STATE_SET.has(value.state)) {
    throw new Error('Native intent state is unsupported.')
  }
  if (!isRecord(value.payload)) throw new Error('Native intent payload must be an object.')
  let payloadBytes: number
  try {
    payloadBytes = new TextEncoder().encode(JSON.stringify(value.payload)).byteLength
  } catch {
    throw new Error('Native intent payload is not serializable.')
  }
  if (payloadBytes > APP_SETTINGS.nativeIntents.maxPayloadBytes) {
    throw new Error('Native intent payload exceeds the local contract.')
  }

  for (const [field, fieldValue] of [
    ['createdAt', value.createdAt],
    ['updatedAt', value.updatedAt],
    ['expiresAt', value.expiresAt],
    ['revision', value.revision],
    ['attemptCount', value.attemptCount],
  ] as const) {
    if (!isFiniteInteger(fieldValue) || fieldValue < 0) {
      throw new Error(`Native intent ${field} is invalid.`)
    }
  }
  const createdAt = value.createdAt as number
  const updatedAt = value.updatedAt as number
  const expiresAt = value.expiresAt as number
  const revision = value.revision as number
  const attemptCount = value.attemptCount as number
  if (revision < 1) throw new Error('Native intent revision must be positive.')
  if (attemptCount > APP_SETTINGS.nativeIntents.maxAttempts) {
    throw new Error('Native intent attempt count exceeds the contract.')
  }
  if (expiresAt <= createdAt || updatedAt < createdAt) {
    throw new Error('Native intent timestamps are inconsistent.')
  }
  if (
    value.acknowledgedRevision !== null
    && (!isFiniteInteger(value.acknowledgedRevision) || value.acknowledgedRevision < 1)
  ) {
    throw new Error('Native intent acknowledged revision is invalid.')
  }
  if (
    (value.state === 'acknowledged' || value.state === 'completed')
    && value.acknowledgedRevision !== revision
  ) {
    throw new Error('Native intent latest revision is not acknowledged.')
  }
  if (value.failureCode !== null && typeof value.failureCode !== 'string') {
    throw new Error('Native intent failure code is invalid.')
  }

  return value as unknown as NativeIntentEnvelope
}

export function nativeIntentRevisionKey(
  envelope: Pick<NativeIntentEnvelope, 'id' | 'revision'>,
): string {
  return `${envelope.id}:${envelope.revision}`
}

export function canAcknowledgeNativeIntent(
  envelope: Pick<NativeIntentEnvelope, 'revision' | 'state'>,
  adoptedRevision: number,
): boolean {
  return envelope.state === 'offered' && envelope.revision === adoptedRevision
}

export async function deriveNativeAccountWorkspaceBinding(
  providerId: string,
  environment: string,
  userId: string,
): Promise<string> {
  if (
    !/^[a-z0-9-]{1,64}$/.test(providerId)
    || !/^[a-z0-9-]{1,64}$/.test(environment)
    || !userId
  ) {
    throw new Error('Native account workspace identity is invalid.')
  }
  const input = new TextEncoder().encode(`${providerId}:${environment}:${userId}`)
  const digest = await crypto.subtle.digest('SHA-256', input)
  const opaqueId = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0')).join('')
  return `account:${opaqueId}`
}

export function nativeCaptureText(envelope: NativeIntentEnvelope): string {
  if (envelope.kind !== 'capture' || typeof envelope.payload.text !== 'string') {
    throw new Error('Native Capture payload is invalid.')
  }
  return envelope.payload.text
}

export function nativeSearchQuery(envelope: NativeIntentEnvelope): string {
  if (envelope.kind !== 'search' || typeof envelope.payload.query !== 'string') {
    throw new Error('Native Search payload is invalid.')
  }
  return envelope.payload.query
}

export function nativeOpenNoteId(envelope: NativeIntentEnvelope): string {
  if (
    envelope.kind !== 'open-note'
    || typeof envelope.payload.noteId !== 'string'
    || !UUID_PATTERN.test(envelope.payload.noteId)
  ) {
    throw new Error('Native Open Note payload is invalid.')
  }
  return envelope.payload.noteId.toLowerCase()
}

export function isNativeShareKind(kind: NativeIntentKind): boolean {
  return kind === 'share-text'
    || kind === 'share-url'
    || kind === 'share-image'
    || kind === 'share-images'
}

function parseNativeImageFiles(files: unknown): NativeShareFileRecord[] {
  if (!Array.isArray(files) || files.length > APP_SETTINGS.nativeIntents.shareMaxImageCount) {
    throw new Error('Native image file list is invalid.')
  }
  const acceptedTypes = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ])
  const seenTokens = new Set<string>()
  let totalSize = 0
  const parsedFiles = files.map((file): NativeShareFileRecord => {
    if (!isRecord(file)) throw new Error('Native image file metadata is invalid.')
    if (typeof file.token !== 'string' || !UUID_PATTERN.test(file.token)) {
      throw new Error('Native image file token is invalid.')
    }
    if (seenTokens.has(file.token)) throw new Error('Native image file token is duplicated.')
    seenTokens.add(file.token)
    if (
      typeof file.name !== 'string'
      || file.name.length < 1
      || file.name.length > 96
      || /[\\/\u0000-\u001f\u007f]/.test(file.name)
    ) {
      throw new Error('Native image file name is invalid.')
    }
    if (typeof file.mimeType !== 'string' || !acceptedTypes.has(file.mimeType)) {
      throw new Error('Native image file type is invalid.')
    }
    if (
      !isFiniteInteger(file.size)
      || file.size < 1
      || file.size > APP_SETTINGS.nativeIntents.shareMaxImageBytes
      || !isFiniteInteger(file.lastModified)
      || file.lastModified < 0
    ) {
      throw new Error('Native image file size or timestamp is invalid.')
    }
    totalSize += file.size
    return file as unknown as NativeShareFileRecord
  })
  if (totalSize > APP_SETTINGS.nativeIntents.shareMaxTotalImageBytes) {
    throw new Error('Native image files exceed the combined limit.')
  }
  return parsedFiles
}

export function nativeSharePayload(envelope: NativeIntentEnvelope): NativeSharePayload {
  if (!isNativeShareKind(envelope.kind)) {
    throw new Error('Native Share kind is invalid.')
  }
  const { body, files, sourceTitle } = envelope.payload
  if (typeof body !== 'string' || body.length > APP_SETTINGS.nativeIntents.shareMaxTextChars) {
    throw new Error('Native Share text is invalid.')
  }
  if (sourceTitle !== undefined && (typeof sourceTitle !== 'string'
    || sourceTitle.length > APP_SETTINGS.nativeIntents.shareMaxTextChars)) {
    throw new Error('Native Share source title is invalid.')
  }
  const parsedFiles = parseNativeImageFiles(files)
  const expectedFileCount = envelope.kind === 'share-image'
    ? 1
    : envelope.kind === 'share-images'
      ? 2
      : 0
  if (
    (envelope.kind === 'share-images' && parsedFiles.length < expectedFileCount)
    || (envelope.kind !== 'share-images' && parsedFiles.length !== expectedFileCount)
  ) {
    throw new Error('Native Share file count does not match its kind.')
  }
  if ((envelope.kind === 'share-text' || envelope.kind === 'share-url') && body.trim().length === 0) {
    throw new Error('Native Share text is empty.')
  }
  if (envelope.kind === 'share-url') {
    try {
      const url = new URL(body)
      if (
        (url.protocol !== 'https:' && url.protocol !== 'http:')
        || !url.hostname
        || /\s/.test(body)
      ) {
        throw new Error()
      }
    } catch {
      throw new Error('Native Share URL is invalid.')
    }
  }
  return { body, files: parsedFiles, ...(sourceTitle === undefined ? {} : { sourceTitle }) }
}

export function nativeEditorImagePayload(
  envelope: NativeIntentEnvelope,
): NativeEditorImagePayload {
  if (envelope.kind !== 'paste-image') {
    throw new Error('Native editor image kind is invalid.')
  }
  const { noteId, files } = envelope.payload
  if (typeof noteId !== 'string' || !UUID_PATTERN.test(noteId)) {
    throw new Error('Native editor image target note is invalid.')
  }
  const parsedFiles = parseNativeImageFiles(files)
  if (parsedFiles.length !== 1) {
    throw new Error('Native editor paste must contain exactly one image.')
  }
  return {
    noteId: noteId.toLowerCase(),
    files: parsedFiles as [NativeShareFileRecord],
  }
}
